import { StyleSheet, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS } from '../../constants';
import { selectDivRadius } from '../../features/appconfig/store/selectors';
import { formatTimeFromSeconds } from '../../helpers/durationParser';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { useEpisodeProgress } from '../../hooks/useEpisodeProgress';
import { useIsEpisodeRead } from '../../hooks/useReadEpisodes';
import { formatEpisodeDate } from '../../localization/datetime';
import i18n from '../../localization/i18n';
import { PodcastEpisodeModel } from '../../models';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';
import { PlayPauseButton } from '../PlayPauseButton';

export function EpisodeControlsSectionTablet(episode: PodcastEpisodeModel) {
  const divRadius = useAppSelector(selectDivRadius);

  // dynamic font sizes
  const textFontSize = useAdjustedFontSize(mvs(12));
  const isRead = useIsEpisodeRead(episode.podcastId, episode.id);

  // when the episode is in progress, show the remaining time (with a minus)
  // instead of the total duration (same behaviour as the play buttons)
  const { isInProgress, remainingSeconds } = useEpisodeProgress(episode);

  return (
    <View style={[styles.episodeControls, { borderRadius: divRadius }]}>
      {/* play controls */}
      <View style={styles.playButtonContainer}>
        <PlayPauseButton size={mvs(40)} iconSize={mvs(20)} podcastEpisode={episode} />
      </View>
      <View style={styles.controlsContent}>
        {/* date */}
        <View style={styles.metaItem}>
          <CustomIcon name="calendar" size={mvs(20)} style={styles.metaIcon} />
          <CustomText
            style={[styles.metaValue, { fontSize: textFontSize }]}
            weight="Regular"
          >
            {formatEpisodeDate(episode.date)}
          </CustomText>
        </View>

        {/* duration */}
        {episode.duration && (
          <View style={styles.metaItem}>
            <CustomIcon name="clock" size={mvs(20)} style={styles.metaIcon} />
            <CustomText
              style={[styles.metaValue, { fontSize: textFontSize }]}
              weight="Regular"
            >
              {isInProgress
                ? `-${formatTimeFromSeconds(remainingSeconds)}`
                : episode.duration}
            </CustomText>
          </View>
        )}

        {/* read indicator */}
        {isRead && (
          <View style={styles.readIndicator}>
            <CustomText
              style={[styles.metaValue, { fontSize: textFontSize }]}
              weight="Regular"
            >
              {i18n.t('podcast.read')}
            </CustomText>
            <CustomIcon name="check" size={mvs(12)} />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  episodeControls: {
    backgroundColor: COLORS.WHITE,
    position: 'relative',
    justifyContent: 'center',
    marginLeft: mvs(20),
    marginTop: mvs(32),
  },
  controlsContent: {
    paddingRight: mvs(24),
    paddingVertical: mvs(12),
    gap: mvs(20),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: mvs(48),
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(8),
  },
  metaIcon: {
    color: COLORS.BLACK,
    opacity: 0.3,
  },
  metaValue: {
    color: COLORS.BLACK,
  },
  readIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(4),
    paddingHorizontal: mvs(8),
    paddingVertical: mvs(4),
    borderRadius: mvs(12),
  },
  playButtonContainer: {
    position: 'absolute',
    left: mvs(-22),
    zIndex: 1,
  },
});
